import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import { COMPANY, IMAGES, STATS, CAPABILITIES, PRODUCTS } from "@/data";
import Logo from "@/components/Logo";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ────────── HERO ────────── */}
      <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end">
            <div className="col-span-12 lg:col-span-7 hu-reveal">
              <div className="hu-section-tag mb-8">Northeastern Ontario · Established 2021</div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-6 lg:gap-8">
                <Logo
                  variant="dark"
                  height={120}
                  className="sm:!h-[160px] lg:!h-[200px] shrink-0"
                />
                <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.95] text-neutral-900">
                  Quality.
                  <br />
                  Service.
                  <br />
                  <span className="text-amber-700">Experience.</span>
                </h1>
              </div>
              <p className="mt-8 text-base lg:text-lg text-neutral-600 leading-relaxed max-w-xl">
                Huron Truss Ltd. is a Northern Ontario manufacturer of engineered wood roof and
                floor trusses, and a full-line distributor of I-joists, LVL, glulam beams,
                engineered columns and connectors — backed by personalized service from quote to delivery.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/quote"
                  data-testid="hero-quote-cta"
                  className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-sm font-medium px-6 py-3.5 rounded-sm transition-colors"
                >
                  Request a Quote <ArrowRight size={16} />
                </Link>
                <Link
                  to="/products"
                  data-testid="hero-products-cta"
                  className="inline-flex items-center gap-2 border border-stone-300 hover:border-neutral-900 text-neutral-900 text-sm font-medium px-6 py-3.5 rounded-sm transition-colors"
                >
                  View Products <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-5 hu-reveal" style={{ animationDelay: "120ms" }}>
              <div className="relative">
                <div className="absolute -top-4 -left-4 right-4 bottom-4 border border-amber-700/40 rounded-sm pointer-events-none" />
                <img
                  src={IMAGES.hero}
                  alt="Engineered wood roof truss assembly"
                  className="w-full aspect-[4/5] object-cover rounded-sm relative z-10"
                  loading="eager"
                />
                <div className="absolute -bottom-6 -right-6 z-20 bg-emerald-950 text-stone-100 p-5 max-w-[200px] rounded-sm border border-emerald-900">
                  <div className="font-display font-bold text-3xl text-amber-500 hu-ticker">80ft</div>
                  <div className="text-xs leading-snug mt-1 text-stone-300">
                    Maximum clear truss span — engineered &amp; stamped
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* tagline marquee */}
        <div className="mt-20 lg:mt-28 border-y border-stone-200 overflow-hidden bg-stone-50">
          <div className="flex hu-marquee-track whitespace-nowrap py-5 font-display font-bold text-2xl lg:text-3xl tracking-tight text-neutral-900">
            {["a", "b"].map((track) => (
              <div key={`marquee-${track}`} className="flex items-center gap-12 px-8">
                {[
                  "Roof Trusses",
                  "Floor Trusses",
                  "I-Joists",
                  "LVL & LSL",
                  "Glulam Beams",
                  "Columns",
                  "Connectors",
                ].map((p) => (
                  <span key={p} className="flex items-center gap-12">
                    <span>{p}</span>
                    <span className="text-amber-700">◆</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────── STATS BENTO ────────── */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-6 items-start">
            <div className="col-span-12 lg:col-span-5">
              <div className="hu-section-tag mb-6">Who we are</div>
              <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tight leading-tight">
                A young company, run by experienced staff.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pt-10">
              <p className="text-neutral-600 leading-relaxed text-base lg:text-lg">
                Huron Truss Ltd. was founded in 2021 by a team with deep roots in the engineered wood
                industry. From a single floor truss to a fully framed roof package, our
                experienced design and production staff turn your plans into engineered,
                code-compliant components — manufactured locally, delivered on time. We work
                directly with building supply yards, builders, framers and homeowners across
                our service region.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 mt-14 border-t border-l border-stone-200">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="border-r border-b border-stone-200 p-6 lg:p-8 bg-white"
                data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="font-display font-bold text-5xl lg:text-6xl text-neutral-900 hu-ticker">
                  {s.value}
                </div>
                <div className="mt-3 text-xs lg:text-sm text-neutral-500 leading-snug uppercase tracking-wider">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────── PRODUCTS ────────── */}
      <section className="py-20 lg:py-28 bg-stone-100/70 border-y border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <div className="hu-section-tag mb-6">What we manufacture &amp; supply</div>
              <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tight leading-tight max-w-2xl">
                Engineered wood components for every framing job.
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="products-section-cta"
              className="inline-flex items-center gap-2 text-sm font-medium text-neutral-900 hover:text-amber-700 transition-colors self-start lg:self-end"
            >
              View full product catalog <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
            {PRODUCTS.map((p) => {
              const Icon = p.icon;
              return (
                <Link
                  key={p.slug}
                  to="/products"
                  data-testid={`product-card-${p.slug}`}
                  className="hu-card-line group bg-stone-50 p-7 lg:p-8 hover:bg-white transition-colors flex flex-col gap-5"
                >
                  <Icon size={32} strokeWidth={1.4} className="text-amber-700" />
                  <div>
                    <h3 className="font-display font-bold text-2xl text-neutral-900 mb-2 tracking-tight">
                      {p.title}
                    </h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{p.summary}</p>
                  </div>
                  <div className="mt-auto pt-4 flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 group-hover:text-amber-700 transition-colors">
                    Details <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ────────── CAPABILITIES ────────── */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-4">
              <div className="hu-section-tag mb-6">How we work</div>
              <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tight leading-tight">
                From sketch to site — engineered every step.
              </h2>
              <p className="mt-6 text-neutral-600 leading-relaxed">
                Every component is designed, stamped, and built to the loading conditions 
                of your specific job — no guesswork.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
                {CAPABILITIES.map((c) => {
                  const Icon = c.icon;
                  return (
                    <div key={c.title} className="bg-white p-7 lg:p-8">
                      <Icon size={28} strokeWidth={1.4} className="text-emerald-950" />
                      <h3 className="font-display font-bold text-xl text-neutral-900 mt-4 mb-2 tracking-tight">
                        {c.title}
                      </h3>
                      <p className="text-sm text-neutral-600 leading-relaxed">{c.body}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────── SERVICE AREA ────────── */}
      <section className="relative py-24 lg:py-32 bg-emerald-950 text-stone-100 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.topoMap})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-emerald-950/85 to-emerald-950/30" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-7">
              <div className="hu-section-tag mb-6 !text-amber-500">Service region</div>
              <h2 className="font-display font-bold text-4xl lg:text-6xl tracking-tight leading-[1.05] text-stone-100">
                Two truss plants. <span className="text-amber-500">One commitment to quality.</span>
              </h2>
              <p className="mt-8 max-w-xl text-stone-300 leading-relaxed text-base lg:text-lg">
                With two manufacturing plants — <span className="text-amber-500 font-medium">Bruce Mines</span> in
                Algoma and <span className="text-amber-500 font-medium">Val Gagné</span> in Cochrane District — we
                cover the region's most demanding sites using roll-off trailers sized to your job.
              </p>
              <Link
                to="/quote"
                data-testid="service-area-cta"
                className="inline-flex items-center gap-2 mt-10 bg-amber-700 hover:bg-amber-600 text-white text-sm font-medium px-6 py-3.5 rounded-sm transition-colors"
              >
                Check delivery to your site <ArrowRight size={16} />
              </Link>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <div className="border border-emerald-800 bg-emerald-900/40 backdrop-blur-sm rounded-sm">
                <div className="p-6 border-b border-emerald-800">
                  <div className="text-xs tracking-widest uppercase text-amber-500">Manufacturing Plants</div>
                  <div className="mt-3 space-y-3">
                    {COMPANY.plants.map((p) => (
                      <div key={p.name} className="flex items-baseline justify-between" data-testid={`plant-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                        <div className="font-display font-bold text-2xl text-stone-100">{p.name}</div>
                        <div className="text-xs text-stone-400">{p.region}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {COMPANY.serviceAreas.map((a, i) => (
                  <div
                    key={a}
                    className={`p-5 flex items-center justify-between ${
                      i !== 0 ? "border-t border-emerald-800" : ""
                    }`}
                    data-testid={`service-area-${a.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div>
                      <div className="text-[10px] tracking-widest uppercase text-amber-500/80">
                        Service area
                      </div>
                      <div className="font-display font-medium text-base text-stone-100 mt-0.5">{a}</div>
                    </div>
                    <ArrowUpRight size={16} className="text-stone-400" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────── CLOSING CTA ────────── */}
      <section className="py-20 lg:py-28 bg-stone-100/70 border-t border-stone-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
          <div className="hu-section-tag !before:hidden mb-6 inline-block">Let's build</div>
          <h2 className="font-display font-bold text-4xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Send us your plans. We'll send back numbers you can build with.
          </h2>
          <p className="mt-6 text-neutral-600 leading-relaxed max-w-xl mx-auto">
            Quotes typically come back within 24–48 hours. Stamped engineered drawings included on
            every project.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/quote"
              data-testid="closing-quote-cta"
              className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-sm font-medium px-6 py-3.5 rounded-sm transition-colors"
            >
              Request a Quote <ArrowRight size={16} />
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="closing-phone-cta"
              className="inline-flex items-center gap-2 border border-stone-300 hover:border-neutral-900 text-neutral-900 text-sm font-medium px-6 py-3.5 rounded-sm transition-colors"
            >
              <Phone size={16} /> <span className="hu-ticker">{COMPANY.phone}</span>
            </a>
            <Link
              to="/contact"
              data-testid="closing-contact-cta"
              className="inline-flex items-center gap-2 border border-stone-300 hover:border-neutral-900 text-neutral-900 text-sm font-medium px-6 py-3.5 rounded-sm transition-colors"
            >
              Contact the team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
